import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { PlugZap, RefreshCw, RotateCcw, Unplug } from 'lucide-react';
import { Banner, BrandIcon, Button, ConfirmDialog, CopyField, Fact, Facts, IconButton, Spinner, StatusPill, Steps, type StepItem } from '@/components/ui';
import { ErrorState } from '@/components/ErrorState';
import { useConnectSearch, useDisconnectSearch, useRecheckSearch, useSiteSearch, useSyncSearch } from '@/hooks/useSearchConsole';
import { useRoles } from '@/hooks/useAuth';
import { toast } from '@/store/toastStore';
import { errorText } from '@/utils/errors';
import { formatRelative } from '@/utils/format';
import { can } from '@/utils/permissions';
import { INDEX_VERDICT, SEARCH_STATE } from '@/utils/seo';
import type { SearchRow, SiteDetail, SiteSearchView } from '@/types/api';

const number = (value: number) => value.toLocaleString('ru-RU');
const percent = (value: number) => `${(value * 100).toLocaleString('ru-RU', { maximumFractionDigits: 1 })}%`;

/** The change against the 28 days before, as a signed percentage; nothing without a base. */
function change(now: number, before: number | undefined): ReactNode {
  if (before === undefined || before === 0) return null;
  const delta = Math.round(((now - before) / before) * 100);
  if (delta === 0) return null;
  return <span className={delta > 0 ? 'tone-healthy' : 'tone-down'}> {delta > 0 ? `+${delta}` : delta}%</span>;
}

function readablePath(address: string): string {
  try {
    const url = new URL(address);
    return decodeURIComponent(`${url.pathname}${url.search}`);
  } catch {
    return address;
  }
}

function RowsTable({ title, rows, page }: { readonly title: string; readonly rows: readonly SearchRow[]; readonly page: boolean }): ReactNode {
  if (rows.length === 0) return null;
  return (
    <div className="table-scroll">
      <table>
        <thead>
          <tr>
            <th scope="col">{title}</th>
            <th scope="col" className="numeric">Клики</th>
            <th scope="col" className="numeric">Показы</th>
            <th scope="col" className="numeric">Позиция</th>
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 10).map((row) => (
            <tr key={row.key}>
              <td className={page ? 'truncate' : ''} title={row.key}>
                {page ? readablePath(row.key) : row.key}
              </td>
              <td className="numeric">{number(row.clicks)}</td>
              <td className="numeric">{number(row.impressions)}</td>
              <td className="numeric">{row.position.toLocaleString('ru-RU', { maximumFractionDigits: 1 })}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Connecting a site as a list of what happens (ADR-0049, ADR-0053): Google's
 * token, the TXT record, Google's checks of it with their count and the next
 * one's time, the property and sitemap, the first read.
 */
function connectSteps(view: SiteSearchView): StepItem[] {
  const state = view.state;
  const past = (states: readonly string[]) => state !== null && !states.includes(state);
  const verified = state === 'verified';
  const failedAtCheck = state === 'failed' && view.attempts > 0;
  return [
    { label: 'Токен Google', state: state === 'requested' ? 'running' : state === 'failed' && !view.domain ? 'failed' : 'done' },
    {
      label: 'Запись TXT',
      state: state === 'requested' ? 'pending' : state === 'awaiting_dns' ? 'running' : state === 'failed' && !failedAtCheck ? 'failed' : 'done',
      detail: state === 'awaiting_dns' ? 'Добавьте запись ниже и нажмите «Проверить запись»' : past(['requested', 'awaiting_dns']) ? (view.dnsByPanel ? 'Добавлена панелью через 2d2s' : 'Добавлена вручную') : undefined,
    },
    {
      label: 'Подтверждение Google',
      state: verified ? 'done' : state === 'verifying' ? 'running' : failedAtCheck ? 'failed' : 'pending',
      detail:
        state === 'verifying'
          ? `Проверок: ${view.attempts} из ${view.maxAttempts}${view.nextAttemptAt ? ` · следующая ${formatRelative(view.nextAttemptAt)}` : ''}`
          : verified && view.verifiedAt
            ? `Подтверждено ${formatRelative(view.verifiedAt)}`
            : undefined,
    },
    { label: 'Ресурс и sitemap', state: verified ? 'done' : 'pending' },
    { label: 'Первые данные', state: verified && view.data ? 'done' : verified ? 'running' : 'pending', detail: verified && !view.data ? 'Google отдаёт данные с задержкой 2–3 дня' : undefined },
  ];
}

/**
 * Google Search Console on a site's SEO tab (ADR-0049): connecting it — the
 * TXT record written by the panel, or shown for a person to add — and what
 * Google says of the site once it is verified.
 */
export function SearchConsoleCard({ site }: { readonly site: SiteDetail }): ReactNode {
  const roles = useRoles();
  const search = useSiteSearch(site.id);
  const [confirm, setConfirm] = useState(false);
  const failed = (what: string) => (error: Error) => toast.error(what, errorText(error));
  const connect = useConnectSearch(site.id, { onSuccess: () => toast.success('Подключение запрошено'), onError: failed('Не удалось подключить') });
  const recheck = useRecheckSearch(site.id, { onSuccess: () => toast.success('Google проверит запись снова'), onError: failed('Не удалось запросить проверку') });
  const sync = useSyncSearch(site.id, { onSuccess: () => toast.success('Запрос отправлен', 'Данные обновятся в течение минуты'), onError: failed('Не удалось запросить обновление') });
  const disconnect = useDisconnectSearch(site.id, { onSuccess: () => setConfirm(false) });
  const mayConnect = can(roles, 'domains.manage');

  const view = search.data;
  const state = view?.state ?? 'none';
  const data = view?.data ?? null;
  const inspected = data?.inspected ?? [];
  const indexed = inspected.filter((page) => page.verdict === 'PASS').length;
  const answered = inspected.filter((page) => page.verdict !== null).length;

  const actions =
    view && view.available && mayConnect ? (
      <div className="row-tight">
        {state === 'none' ? (
          <Button size="sm" variant="primary" icon={PlugZap} busy={connect.isPending} disabled={!site.primaryDomain} onClick={() => connect.mutate()}>
            Подключить
          </Button>
        ) : null}
        {state === 'awaiting_dns' || state === 'failed' ? (
          <Button size="sm" icon={RotateCcw} busy={recheck.isPending} onClick={() => recheck.mutate()}>
            {state === 'failed' ? 'Повторить' : 'Проверить запись'}
          </Button>
        ) : null}
        {state === 'verified' ? (
          <Button size="sm" icon={RefreshCw} busy={sync.isPending} disabled={view.syncRequested} onClick={() => sync.mutate()}>
            {view.syncRequested ? 'Обновляется' : 'Обновить'}
          </Button>
        ) : null}
        {state !== 'none' ? <IconButton size="sm" variant="ghost" icon={Unplug} label="Отключить" onClick={() => setConfirm(true)} /> : null}
      </div>
    ) : null;

  return (
    <section className="card">
      <header className="card-head">
        <h3 className="card-title">
          <BrandIcon name="googlesearchconsole" size={16} />
          Google Search Console
        </h3>
        <div className="row-tight">
          {view?.available ? <StatusPill map={SEARCH_STATE} status={state} /> : null}
          {actions}
        </div>
      </header>
      <div className="card-body stack">
        {search.isPending ? (
          <Spinner />
        ) : search.isError ? (
          <ErrorState error={search.error} />
        ) : !view?.available ? (
          <p className="muted">
            Ключ Google не задан.{' '}
            {can(roles, 'secrets.manage') ? <Link to="/integrations/google">Подключить на странице «Интеграции»</Link> : 'Его задаёт владелец или администратор.'}
          </p>
        ) : state === 'none' ? (
          <p className="muted">Домен подтверждается записью TXT, sitemap отправляется автоматически.</p>
        ) : (
          <>
            {state !== 'verified' || !data ? <Steps items={connectSteps(view)} label="Подключение к Search Console" /> : null}
            {view.txtRecord ? (
              <div className="stack">
                <p className="muted">Панель не может менять DNS этого домена. Добавьте запись TXT и нажмите «Проверить запись».</p>
                <CopyField label={`Имя (${view.txtRecord.name})`} value="@" />
                <CopyField label="Значение" value={view.txtRecord.value} />
              </div>
            ) : null}
            {view.error ? <Banner tone={state === 'failed' ? 'danger' : 'warning'}>{view.error}</Banner> : null}

            {data ? (
              <>
                <Facts>
                  <Fact label="Клики">
                    {data.totals ? number(data.totals.clicks) : '0'}
                    {data.totals ? change(data.totals.clicks, data.previous?.clicks) : null}
                    <span className="muted"> · {data.period.start.slice(5)} – {data.period.end.slice(5)}</span>
                  </Fact>
                  <Fact label="Показы">
                    {data.totals ? number(data.totals.impressions) : '0'}
                    {data.totals ? change(data.totals.impressions, data.previous?.impressions) : null}
                  </Fact>
                  <Fact label="CTR и позиция">{data.totals ? `${percent(data.totals.ctr)} · ${data.totals.position.toLocaleString('ru-RU', { maximumFractionDigits: 1 })}` : <span className="faint">нет показов</span>}</Fact>
                  <Fact label="Индексация">
                    {answered > 0 ? `${indexed} из ${answered} проверенных страниц в индексе` : <span className="faint">нет данных</span>}
                  </Fact>
                  <Fact label="Sitemap">
                    {data.sitemaps.length > 0 ? (
                      <ul className="list-plain">
                        {data.sitemaps.map((sitemap) => (
                          <li key={sitemap.path}>
                            <span className="mono">{readablePath(sitemap.path)}</span>
                            <span className="muted">
                              {sitemap.submitted !== null ? ` · адресов ${number(sitemap.submitted)}` : ''}
                              {sitemap.errors > 0 ? ` · ошибок ${sitemap.errors}` : ''}
                              {sitemap.isPending ? ' · обрабатывается' : ''}
                            </span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <span className="faint">не отправлен</span>
                    )}
                  </Fact>
                  {view.syncedAt ? <Fact label="Обновлено">{formatRelative(view.syncedAt)}</Fact> : null}
                </Facts>

                {inspected.length > 0 ? (
                  <div className="table-scroll">
                    <table>
                      <thead>
                        <tr>
                          <th scope="col">Страница</th>
                          <th scope="col">Google</th>
                          <th scope="col">Причина</th>
                        </tr>
                      </thead>
                      <tbody>
                        {inspected.map((page) => (
                          <tr key={page.url}>
                            <td className="truncate" title={page.url}>
                              {readablePath(page.url)}
                            </td>
                            <td>
                              <StatusPill map={INDEX_VERDICT} status={page.verdict ?? 'unknown'} />
                            </td>
                            <td className="muted">{page.coverageState ?? page.error ?? '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : null}

                <div className="split">
                  <RowsTable title="Запрос" rows={data.queries} page={false} />
                  <RowsTable title="Страница" rows={data.pages} page />
                </div>
              </>
            ) : state === 'verified' ? (
              <p className="muted">Данные появятся после первого чтения.</p>
            ) : null}
          </>
        )}
      </div>
      <ConfirmDialog
        open={confirm}
        title="Отключить Search Console?"
        description="Панель перестанет читать данные. Ресурс в Google и запись TXT останутся."
        confirmLabel="Отключить"
        busy={disconnect.isPending}
        error={disconnect.isError ? errorText(disconnect.error) : undefined}
        onConfirm={() => disconnect.mutate()}
        onClose={() => setConfirm(false)}
      />
    </section>
  );
}
